/**
 * Event Effect Mapper for Poker Reborn Effects Engine
 *
 * Maps game events to effect cues. This is the bridge between the
 * GameEventBus (what happened) and the EffectsDirector (what to show).
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 3.1
 */

import { log } from '@/utils/log';
import type { EffectCue } from '../core/EffectCue';
import type { GameEventName, GameEventPayload } from '../events/GameEvents';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Handler function that converts an event payload to effect cues.
 * Returns an array of cues (can be empty if no effects should play).
 */
export type EventEffectHandler<E extends GameEventName> = (
  data: GameEventPayload<E>
) => EffectCue[];

/**
 * Theme-specific mapping overrides.
 */
export interface ThemeMappingOverrides {
  /** Theme identifier */
  themeId: string;
  /** Event handlers that override defaults */
  handlers: Partial<{
    [E in GameEventName]: EventEffectHandler<E>;
  }>;
}

// =============================================================================
// EVENT EFFECT MAPPER CLASS (P3.1.1-P3.1.4)
// =============================================================================

/**
 * Maps game events to effect cues.
 *
 * The mapper receives game events and returns arrays of EffectCues
 * that describe what effects should play. Themes can override
 * mappings to customize the visual experience.
 *
 * This is a singleton - use EventEffectMapper.getInstance()
 */
export class EventEffectMapper {
  private static _instance: EventEffectMapper | null = null;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Default event handlers */
  private _handlers: Map<GameEventName, EventEffectHandler<any>> = new Map();

  /** Theme-specific overrides */
  private _themeOverrides: Map<string, ThemeMappingOverrides> = new Map();

  /** Currently active theme */
  private _activeThemeId: string | null = null;

  /** Whether mapping is enabled */
  private _enabled = true;

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): EventEffectMapper {
    if (!EventEffectMapper._instance) {
      EventEffectMapper._instance = new EventEffectMapper();
    }
    return EventEffectMapper._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    EventEffectMapper._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  /**
   * Enable or disable effect mapping.
   */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
  }

  /**
   * Check if mapping is enabled.
   */
  get enabled(): boolean {
    return this._enabled;
  }

  // ---------------------------------------------------------------------------
  // Registration (P3.1.2)
  // ---------------------------------------------------------------------------

  /**
   * Register a default handler for an event type.
   *
   * @param event Event name to handle
   * @param handler Function that converts event data to effect cues
   */
  map<E extends GameEventName>(event: E, handler: EventEffectHandler<E>): void {
    this._handlers.set(event, handler);
    log.effects.debug`EventEffectMapper: registered handler for "${event}"`;
  }

  /**
   * Register multiple handlers at once.
   */
  mapMany(handlers: Partial<{ [E in GameEventName]: EventEffectHandler<E> }>): void {
    for (const [event, handler] of Object.entries(handlers)) {
      if (handler) {
        this.map(event as GameEventName, handler as EventEffectHandler<any>);
      }
    }
  }

  /**
   * Unregister a handler for an event type.
   */
  unmap(event: GameEventName): boolean {
    return this._handlers.delete(event);
  }

  /**
   * Check if a handler is registered for an event.
   */
  hasHandler(event: GameEventName): boolean {
    return this._handlers.has(event) || this.hasThemeHandler(event);
  }

  /**
   * Get all registered event names.
   */
  getRegisteredEvents(): GameEventName[] {
    return Array.from(this._handlers.keys());
  }

  // ---------------------------------------------------------------------------
  // Theme Support (P3.1.4)
  // ---------------------------------------------------------------------------

  /**
   * Register theme-specific mapping overrides.
   */
  registerThemeOverrides(
    themeId: string,
    handlers: Partial<{ [E in GameEventName]: EventEffectHandler<E> }>
  ): void {
    const overrides: ThemeMappingOverrides = {
      themeId,
      handlers,
    };
    this._themeOverrides.set(themeId, overrides);
    log.effects.debug`EventEffectMapper: registered ${Object.keys(handlers).length} overrides for theme "${themeId}"`;
  }

  /**
   * Set the active theme.
   */
  setActiveTheme(themeId: string | null): void {
    this._activeThemeId = themeId;
    log.effects.debug`EventEffectMapper: active theme set to "${themeId}"`;
  }

  /**
   * Get the active theme ID.
   */
  get activeThemeId(): string | null {
    return this._activeThemeId;
  }

  /**
   * Check if current theme has a handler for an event.
   */
  private hasThemeHandler(event: GameEventName): boolean {
    if (!this._activeThemeId) return false;
    const overrides = this._themeOverrides.get(this._activeThemeId);
    return overrides?.handlers[event] !== undefined;
  }

  /**
   * Get theme handler for an event.
   */
  private getThemeHandler<E extends GameEventName>(
    event: E
  ): EventEffectHandler<E> | undefined {
    if (!this._activeThemeId) return undefined;
    const overrides = this._themeOverrides.get(this._activeThemeId);
    return overrides?.handlers[event];
  }

  // ---------------------------------------------------------------------------
  // Effect Retrieval (P3.1.3)
  // ---------------------------------------------------------------------------

  /**
   * Get effect cues for an event.
   *
   * Priority:
   * 1. Theme-specific handler (if active theme has one)
   * 2. Default handler
   * 3. Empty array (no effects)
   *
   * @param event Event name
   * @param data Event payload
   * @returns Array of effect cues to play
   */
  getEffects<E extends GameEventName>(event: E, data: GameEventPayload<E>): EffectCue[] {
    if (!this._enabled) {
      return [];
    }

    // Try theme handler first
    const themeHandler = this.getThemeHandler(event);
    if (themeHandler) {
      try {
        return themeHandler(data);
      } catch (error) {
        log.effects.error`EventEffectMapper: theme handler error for "${event}": ${error}`;
        // Fall through to default handler
      }
    }

    // Try default handler
    const defaultHandler = this._handlers.get(event);
    if (defaultHandler) {
      try {
        return defaultHandler(data);
      } catch (error) {
        log.effects.error`EventEffectMapper: handler error for "${event}": ${error}`;
        return [];
      }
    }

    // No handler registered
    return [];
  }

  /**
   * Check if an event would produce any effects.
   * Useful for debugging/testing.
   */
  wouldProduceEffects<E extends GameEventName>(
    event: E,
    data: GameEventPayload<E>
  ): boolean {
    return this.getEffects(event, data).length > 0;
  }

  // ---------------------------------------------------------------------------
  // Clear / Reset
  // ---------------------------------------------------------------------------

  /**
   * Clear all handlers and theme overrides.
   */
  clear(): void {
    this._handlers.clear();
    this._themeOverrides.clear();
    this._activeThemeId = null;
  }

  /**
   * Clear only theme overrides.
   */
  clearThemeOverrides(): void {
    this._themeOverrides.clear();
    this._activeThemeId = null;
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global event effect mapper instance.
 */
export const eventEffectMapper = EventEffectMapper.getInstance();
